import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, UserPlus, CheckCircle, CreditCard, ChevronRight } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import Button from '../ui/Button';
import CreateOrganization from './CreateOrganizationForm';
import SessionRevocationCard from './SessionRevocationCard';
import apiService from '../../services/api';
import { isIOSApp } from '../../utils/platform';
import { useAuth } from '../../context/AuthContext';
import { hasBillingAccess } from '../../utils/auth';

interface OrgOption {
  id: number;
  name: string;
}

interface UserOption {
  id: number;
  username: string;
  email: string;
}

// Under the typed-license model only operational RBAC roles remain.
// Feature-specific roles (leave_approver / timesheet_approver /
// payroll_manager / human_resources / spray_creator / spray_operator) are
// now license seats managed from the Subscription Management page.
const ROLES = ['employee', 'admin', 'manager', 'advisor'];

function SuperAdminPanel() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showCreateOrg, setShowCreateOrg] = useState(false);
  const [createOrgSuccess, setCreateOrgSuccess] = useState<string | null>(null);

  const [orgs, setOrgs] = useState<OrgOption[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedRole, setSelectedRole] = useState('employee');
  const [addMemberLoading, setAddMemberLoading] = useState(false);
  const [addMemberError, setAddMemberError] = useState<string | null>(null);
  const [addMemberSuccess, setAddMemberSuccess] = useState<string | null>(null);

  useEffect(() => {
    apiService.get<{ organisations: any[]; pagination: any }>('/organisations?limit=200').then(res => {
      if (res.success && res.data) {
        const list = (res.data as any).organisations ?? [];
        setOrgs(list.map((o: any) => ({ id: o.id, name: o.name })));
      }
    }).catch(() => {});

    // The one screen that is deliberately platform-wide: adding any user to any
    // org. Everywhere else the list stays scoped to the active organisation.
    apiService.get<any[]>('/users?scope=all').then(res => {
      if (res.success && res.data) {
        const list = Array.isArray(res.data) ? res.data : [];
        setUsers(list.map((u: any) => ({ id: u.id, username: u.username, email: u.email })));
      }
    }).catch(() => {});
  }, [createOrgSuccess]);

  const handleAddMember = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedOrg || !selectedUser) return;
    setAddMemberLoading(true);
    setAddMemberError(null);
    setAddMemberSuccess(null);
    try {
      const res = await apiService.post(`/organisations/${selectedOrg}/members`, {
        user_id: Number(selectedUser),
        role_name: selectedRole,
      });
      if (res.success) {
        const orgName = orgs.find(o => o.id === Number(selectedOrg))?.name ?? 'org';
        const userName = users.find(u => u.id === Number(selectedUser))?.username ?? 'user';
        setAddMemberSuccess(`Added ${userName} to ${orgName} as ${selectedRole}`);
        setSelectedUser('');
      } else {
        setAddMemberError((res as any).error?.message ?? 'Failed to add member');
      }
    } catch (err: any) {
      setAddMemberError(err.response?.data?.error?.message ?? err.message ?? 'Failed to add member');
    } finally {
      setAddMemberLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 px-1">
        <div className="h-8 w-8 rounded-lg bg-amber-100 flex items-center justify-center">
          <Shield className="h-4 w-4 text-amber-600" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-neutral-900">Super Admin</h2>
          <p className="text-sm text-neutral-500">Cross-organisation management</p>
        </div>
      </div>

      {/* Billing shortcut — only for our own organisation's operators. A
          super admin of any other org is refused by the server, so the way
          in is not shown to them. */}
      {hasBillingAccess(user) && (
      <button
        onClick={() => navigate('/billing')}
        className="w-full flex items-center justify-between bg-white border border-amber-200 rounded-xl px-5 py-4 hover:border-amber-400 hover:shadow-sm transition-all text-left"
      >
        <div className="flex items-center gap-3">
          <div className="bg-amber-100 p-2.5 rounded-lg">
            <CreditCard className="h-5 w-5 text-amber-600" />
          </div>
          <div>
            <p className="font-semibold text-neutral-900">Billing</p>
            <p className="text-sm text-neutral-500">Onboard customers, trials, offers and invoices</p>
          </div>
        </div>
        <ChevronRight className="h-4 w-4 text-neutral-400 shrink-0" />
      </button>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Create Organisation — hidden on iOS (App Store 3.1.1) */}
        {!isIOSApp() && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Create Organisation</CardTitle>
          </CardHeader>
          <CardContent>
            {createOrgSuccess ? (
              <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                <p className="text-sm text-green-800">{createOrgSuccess}</p>
              </div>
            ) : showCreateOrg ? (
              <CreateOrganization
                onSuccess={(data) => {
                  const name = data?.organisation?.name ?? 'New organisation';
                  setCreateOrgSuccess(`"${name}" created. You are now an admin — use the org switcher to switch to it.`);
                  setShowCreateOrg(false);
                }}
                onSkip={() => setShowCreateOrg(false)}
              />
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-neutral-600">
                  Create a new organisation. You will be automatically added as admin and can then switch to it using the org switcher in the header.
                </p>
                <Button onClick={() => setShowCreateOrg(true)} variant="primary" size="md">
                  New Organisation
                </Button>
              </div>
            )}
            {createOrgSuccess && (
              <button
                className="mt-3 text-sm text-primary-600 hover:underline"
                onClick={() => { setCreateOrgSuccess(null); setShowCreateOrg(false); }}
              >
                Create another
              </button>
            )}
          </CardContent>
        </Card>
        )}

        {/* Add User to Org */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <UserPlus className="h-4 w-4" />
              Add User to Organisation
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAddMember} className="space-y-4">
              {addMemberError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
                  {addMemberError}
                </div>
              )}
              {addMemberSuccess && (
                <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  <p className="text-sm text-green-800">{addMemberSuccess}</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Organisation</label>
                <select
                  value={selectedOrg}
                  onChange={e => setSelectedOrg(e.target.value)}
                  required
                  className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Select organisation…</option>
                  {orgs.map(o => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">User</label>
                <select
                  value={selectedUser}
                  onChange={e => setSelectedUser(e.target.value)}
                  required
                  className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Select user…</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>{u.username} ({u.email})</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Role</label>
                <select
                  value={selectedRole}
                  onChange={e => setSelectedRole(e.target.value)}
                  className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {ROLES.map(r => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>

              <Button type="submit" variant="primary" size="md" disabled={addMemberLoading || !selectedOrg || !selectedUser}>
                {addMemberLoading ? 'Adding…' : 'Add to Organisation'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Session Revocation */}
      <SessionRevocationCard orgs={orgs} users={users} />
    </div>
  );
}

export default SuperAdminPanel;
